<script setup>
import { inject, onBeforeUnmount, onMounted, ref, watch } from "vue"
import { api, post } from "../api"
import DualTime from "../components/DualTime.vue"
import { formatDate, IMPORT_KIND_LABELS } from "../format"

const me = inject("me")
const runs = ref([]), connections = ref([]), allConnections = ref([]), queueing = ref(false), error = ref(""), notice = ref("")
const section = ref("imports"), jobs = ref([]), jobCounts = ref({}), jobQueues = ref([]), jobsTotal = ref(0), jobsLoading = ref(false), jobsError = ref("")
const jobFilters = ref({ status: "", queue: "", q: "" })
let jobsTimer
const dateKey = date => `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}-${String(date.getDate()).padStart(2, "0")}`
const today = new Date(), weekAgo = new Date(Date.now() - 6 * 86400000)
const range = ref({ kind: "players", connection_id: "", from: dateKey(weekAgo), to: dateKey(today) })

async function load() {
  runs.value = (await api("/import_runs")).items.map(run => ({
    ...run,
    kind: IMPORT_KIND_LABELS[run.kind] || run.kind,
    period_date: run.period_date ? formatDate(`${run.period_date}T00:00:00`, { dateOnly: true }) : null
  }))
}

async function loadJobs() {
  if (me.value?.role !== "admin") return
  jobsLoading.value = true
  jobsError.value = ""
  try {
    const params = new URLSearchParams({ per_page: "200" })
    Object.entries(jobFilters.value).forEach(([key, value]) => { if (value) params.set(key, value) })
    const payload = await api(`/solid_queue_jobs?${params}`)
    jobs.value = payload.items
    jobCounts.value = payload.counts
    jobQueues.value = payload.queues
    jobsTotal.value = payload.total
  } catch (exception) {
    jobsError.value = exception.message
  } finally {
    jobsLoading.value = false
  }
}

async function loadConnections() {
  allConnections.value = (await api("/integration_connections")).filter(connection => connection.active)
  updateConnections()
  if (!range.value.connection_id && connections.value.length) range.value.connection_id = connections.value[0].id
}
function updateConnections() {
  connections.value = allConnections.value.filter(connection => range.value.kind !== "traffic" || connection.provider === "affilka")
  if (!connections.value.some(connection => connection.id === Number(range.value.connection_id))) range.value.connection_id = connections.value[0]?.id || ""
}

async function retry(id) {
  await post(`/import_runs/${id}/retry`, {})
  await load()
}

async function reimportRange() {
  error.value = ""
  notice.value = ""
  if (!range.value.connection_id || !range.value.from || !range.value.to) {
    error.value = "Выберите интеграцию и обе даты."
    return
  }
  queueing.value = true
  try {
    const endpoint = range.value.kind === "traffic" ? "reimport_traffic" : "reimport_players"
    await post(`/integration_connections/${range.value.connection_id}/${endpoint}`, { from: range.value.from, to: range.value.to })
    const kindLabel = range.value.kind === "traffic" ? "трафика" : "данных игроков"
    notice.value = `Реимпорт ${kindLabel} поставлен в очередь. Каждый день появится в журнале отдельным запуском.`
    await load()
  } catch (exception) {
    error.value = exception.message
  } finally {
    queueing.value = false
  }
}

function switchSection(value) {
  section.value = value
  if (value === "jobs") loadJobs()
}

function resetJobFilters() {
  jobFilters.value = { status: "", queue: "", q: "" }
  loadJobs()
}

function jobStatusLabel(status) {
  return { ready: "В очереди", running: "Выполняется", scheduled: "Запланирована", blocked: "Заблокирована", failed: "Ошибка", pending: "Подготовка" }[status] || status
}

onMounted(async () => {
  await Promise.all([load(), loadConnections()])
  jobsTimer = window.setInterval(() => { if (section.value === "jobs") loadJobs() }, 5000)
})
onBeforeUnmount(() => window.clearInterval(jobsTimer))
watch(() => range.value.kind, updateConnections)
</script>

<template>
  <section class="page">
    <div class="page-heading">
      <div><small>Интеграции</small><h1>{{ section === "jobs" ? "Очередь джоб" : "Импорт" }}</h1></div>
      <button @click="section === 'jobs' ? loadJobs() : load()">Обновить</button>
    </div>

    <nav v-if="me?.role === 'admin'" class="settings-nav imports-nav">
      <button :class="{ active: section === 'imports' }" @click="switchSection('imports')">Журнал импортов</button>
      <button :class="{ active: section === 'jobs' }" @click="switchSection('jobs')">Solid Queue <span v-if="jobCounts.total">{{ jobCounts.total }}</span></button>
    </nav>

    <template v-if="section === 'imports'">
      <form class="panel player-range-import" @submit.prevent="reimportRange">
        <div class="player-range-import-copy">
          <strong>Реимпорт данных</strong>
          <small>Дни обрабатываются последовательно: все страницы одного дня, затем следующий день.</small>
        </div>
        <label>Тип данных<select v-model="range.kind"><option value="players">Данные игроков</option><option value="traffic">Трафик</option></select></label>
        <label>Интеграция<select v-model="range.connection_id" required><option value="" disabled>Выберите</option><option v-for="connection in connections" :key="connection.id" :value="connection.id">{{ connection.name }}</option></select></label>
        <label>Дата от<input v-model="range.from" type="date" :max="range.to" required></label>
        <label>Дата до<input v-model="range.to" type="date" :min="range.from" required></label>
        <button class="primary" :disabled="queueing || !connections.length">{{ queueing ? "Добавление…" : "Запустить" }}</button>
      </form>
      <div v-if="error" class="notice error">{{ error }}</div>
      <div v-if="notice" class="notice">{{ notice }}</div>

      <div class="panel table-panel">
        <table><thead><tr><th>Запуск</th><th>Подключение</th><th>Период</th><th>Строк</th><th>Статус</th><th></th></tr></thead><tbody><tr v-for="run in runs" :key="run.id"><td><strong>{{ run.kind }}</strong><small><DualTime :value="run.created_at"/></small></td><td>{{ run.connection_name }}</td><td>{{ run.period_date || "—" }}</td><td>{{ run.records_count }}</td><td><span class="status" :class="run.status">{{ run.status }}</span><small class="error">{{ run.error_message }}</small></td><td><button v-if="run.status==='failed'" @click="retry(run.id)">Повторить</button></td></tr></tbody></table>
      </div>
    </template>

    <template v-else>
      <div class="queue-summary">
        <button v-for="status in ['running', 'ready', 'scheduled', 'blocked', 'failed']" :key="status" class="panel" :class="{ active: jobFilters.status === status }" @click="jobFilters.status = jobFilters.status === status ? '' : status; loadJobs()">
          <small>{{ jobStatusLabel(status) }}</small><strong>{{ jobCounts[status] || 0 }}</strong>
        </button>
      </div>
      <form class="panel queue-filters" @submit.prevent="loadJobs">
        <label>Поиск<input v-model="jobFilters.q" placeholder="Класс или Active Job ID"></label>
        <label>Статус<select v-model="jobFilters.status"><option value="">Все текущие</option><option v-for="status in ['running', 'ready', 'scheduled', 'blocked', 'failed', 'pending']" :key="status" :value="status">{{ jobStatusLabel(status) }}</option></select></label>
        <label>Очередь<select v-model="jobFilters.queue"><option value="">Все очереди</option><option v-for="queue in jobQueues" :key="queue" :value="queue">{{ queue }}</option></select></label>
        <button class="primary" :disabled="jobsLoading">{{ jobsLoading ? "Загрузка…" : "Применить" }}</button>
        <button type="button" @click="resetJobFilters">Сбросить</button>
      </form>
      <div v-if="jobsError" class="notice error">{{ jobsError }}</div>
      <div class="panel table-panel queue-table">
        <table><thead><tr><th>Джоба</th><th>Очередь</th><th>Статус</th><th>Создана</th><th>Запуск</th><th>Приоритет</th></tr></thead>
          <tbody>
            <tr v-for="job in jobs" :key="job.id">
              <td><strong>{{ job.class_name }}</strong><small>#{{ job.id }} · {{ job.active_job_id }}</small><small v-if="job.error_message" class="error">{{ job.error_class }}: {{ job.error_message }}</small></td>
              <td><code>{{ job.queue_name }}</code></td>
              <td><span class="job-status" :class="job.status">{{ jobStatusLabel(job.status) }}</span></td>
              <td><DualTime :value="job.created_at"/></td>
              <td><DualTime :value="job.started_at || job.scheduled_at"/></td>
              <td>{{ job.priority }}</td>
            </tr>
            <tr v-if="!jobs.length && !jobsLoading"><td colspan="6" class="queue-empty">Текущих джоб не найдено</td></tr>
          </tbody>
        </table>
        <footer class="queue-table-footer">Показано {{ jobs.length }} из {{ jobsTotal }} · автообновление каждые 5 секунд</footer>
      </div>
    </template>
  </section>
</template>
